import { useCallback, useMemo, useState } from 'react';

import { validateApiResponse } from '../../../services';
import { listRoles } from '../../../services/rolesService';
import { ROLE_TYPES } from '../constants/roles';
import { useAsyncLoad } from './useAsyncLoad';

/** API role (snake_case or camelCase) → the role the screens render. */
export const mapApiRoleToUi = (api) => {
  const permissions = api.permissions ?? [];
  const type = api.type;
  return {
    id: api.id,
    name: api.name,
    description: api.description ?? '',
    type,
    isSystem: type === ROLE_TYPES.SYSTEM,
    studios: api.studios ?? [],
    permissions,
    permissionCount: permissions.length,
    memberCount: api.member_count ?? api.memberCount ?? 0,
    membersPreview: api.members_preview ?? api.membersPreview ?? [],
  };
};

const NO_ROLES = Object.freeze([]);

/**
 * Roles for the Roles tab, grouped `{ system, custom }`: `{ groups, roles, isLoading, error,
 * search, setSearch, refetch }`. `search` is the COMMITTED query (the page commits it after the
 * typing pause, or on Enter). The previous list stays on screen while a new search loads.
 */
export const useRoles = () => {
  const [search, setSearch] = useState('');
  const fetchRoles = useCallback(
    () =>
      listRoles({ search: { query: search, fields: ['name', 'description'] } }).then((res) =>
        validateApiResponse(res, 'Could not load the roles').body.items.map(mapApiRoleToUi),
      ),
    [search],
  );
  const { data: roles, isLoading, error, reload } = useAsyncLoad(fetchRoles, NO_ROLES);

  return useMemo(
    () => ({
      roles,
      groups: {
        system: roles.filter((role) => role.isSystem),
        custom: roles.filter((role) => !role.isSystem),
      },
      isLoading,
      error,
      search,
      setSearch,
      refetch: reload,
    }),
    [roles, isLoading, error, search, reload],
  );
};
